/**
 * Guide-focused data operations (Guide Expert backfill).
 */
import React, { useCallback, useState } from "react";
import { useSnackbarController } from "@firecms/core";
import { Button, Paper, Typography } from "@firecms/ui";
import { fetchWithAuth } from "./fetchWithAuth";
import { BatchResultBar } from "./BatchResultBar";
import type { BatchItemResult, DocListItem } from "./types";

export interface GuideDataToolsProps {
  baseUrl: string;
  getToken: () => Promise<string | null>;
  batchDelaySec: number;
}

async function runServerBatch(
  baseUrl: string,
  token: string,
  operation: string,
  document_ids: string[],
  delayMs: number,
  force?: boolean
): Promise<{ ok: boolean; results: BatchItemResult[]; error?: string }> {
  const res = await fetchWithAuth(`${baseUrl}/api/batch/run`, token, {
    operation,
    document_ids,
    delay_ms: delayMs,
    force: force ?? false,
  });
  const data = (await res.json()) as {
    results?: Array<{ id: string; ok: boolean; error?: string }>;
    error?: string;
  };
  if (!res.ok) {
    return { ok: false, results: [], error: data.error ?? "Batch failed" };
  }
  const results = (data.results ?? []).map((r) => ({
    id: r.id,
    ok: r.ok,
    error: r.error,
  }));
  return { ok: true, results };
}

export function GuideDataTools({ baseUrl, getToken, batchDelaySec }: GuideDataToolsProps) {
  const snackbar = useSnackbarController();
  const delayMs = batchDelaySec * 1000;

  const [incomplete, setIncomplete] = useState<DocListItem[] | null>(null);
  const [loadingIncomplete, setLoadingIncomplete] = useState(false);
  const [running, setRunning] = useState<string | "all" | null>(null);
  const [batchResults, setBatchResults] = useState<BatchItemResult[] | null>(null);

  const loadIncomplete = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setLoadingIncomplete(true);
    setBatchResults(null);
    try {
      const res = await fetchWithAuth(`${baseUrl}/api/guides/incomplete`, token, {});
      const data = (await res.json()) as { guides?: DocListItem[]; error?: string };
      if (!res.ok) {
        snackbar.open({ type: "error", message: data.error ?? "Failed to fetch" });
        return;
      }
      setIncomplete(data.guides ?? []);
      snackbar.open({
        type: "success",
        message: `Found ${(data.guides ?? []).length} guides missing EN title or description`,
      });
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Request failed",
      });
    } finally {
      setLoadingIncomplete(false);
    }
  }, [baseUrl, getToken, snackbar]);

  const runGuideExpert = useCallback(
    async (docId: string) => {
      const token = await getToken();
      if (!token) return;
      setRunning(docId);
      try {
        const res = await fetchWithAuth(`${baseUrl}/run-guide-expert`, token, { document_id: docId });
        const data = (await res.json()) as { error?: string; skipped?: boolean };
        if (!res.ok) {
          snackbar.open({ type: "error", message: data.error ?? "Failed" });
          return;
        }
        snackbar.open({
          type: "success",
          message: data.skipped ? "Guide already complete" : `Content generated for ${docId}`,
        });
        setIncomplete((prev) => prev?.filter((d) => d._id !== docId) ?? null);
      } catch (err) {
        snackbar.open({
          type: "error",
          message: err instanceof Error ? err.message : "Request failed",
        });
      } finally {
        setRunning((p) => (p === docId ? null : p));
      }
    },
    [baseUrl, getToken, snackbar]
  );

  const runAll = useCallback(async () => {
    if (!incomplete?.length) return;
    setRunning("all");
    setBatchResults(null);
    const token = await getToken();
    if (!token) {
      setRunning(null);
      return;
    }
    const { ok, results, error } = await runServerBatch(
      baseUrl,
      token,
      "run_guide_expert",
      incomplete.map((d) => d._id),
      delayMs,
      false
    );
    setRunning(null);
    if (!ok) {
      snackbar.open({ type: "error", message: error ?? "Batch failed" });
      return;
    }
    setBatchResults(results);
    const failedIds = new Set(results.filter((r) => !r.ok).map((r) => r.id));
    setIncomplete((prev) => prev?.filter((d) => failedIds.has(d._id)) ?? null);
    const successCount = results.filter((r) => r.ok).length;
    snackbar.open({
      type: results.some((r) => !r.ok) ? "error" : "success",
      message: `Guide Expert: ${successCount}/${results.length} succeeded`,
    });
  }, [incomplete, baseUrl, getToken, snackbar, delayMs]);

  const retryFailed = useCallback(async () => {
    if (!batchResults?.length) return;
    const ids = batchResults.filter((r) => !r.ok).map((r) => r.id);
    if (!ids.length) return;
    setRunning("all");
    setBatchResults(null);
    const token = await getToken();
    if (!token) {
      setRunning(null);
      return;
    }
    const { ok, results, error } = await runServerBatch(
      baseUrl,
      token,
      "run_guide_expert",
      ids,
      delayMs,
      false
    );
    setRunning(null);
    if (!ok) {
      snackbar.open({ type: "error", message: error ?? "Batch failed" });
      return;
    }
    setBatchResults(results);
    const failedIds = new Set(results.filter((r) => !r.ok).map((r) => r.id));
    setIncomplete((prev) => {
      const base = prev ?? ids.map((id) => ({ _id: id, name: id }));
      return base.filter((d) => failedIds.has(d._id));
    });
    const successCount = results.filter((r) => r.ok).length;
    snackbar.open({
      type: results.some((r) => !r.ok) ? "error" : "success",
      message: `Retry: ${successCount}/${results.length} succeeded`,
    });
  }, [batchResults, baseUrl, getToken, snackbar, delayMs]);

  return (
    <div className="space-y-6">
      <Paper className="p-6">
        <Typography variant="subtitle1" className="font-semibold mb-2">
          Backfill guide content
        </Typography>
        <Typography variant="body2" className="mb-4 text-gray-600">
          Guides missing English title or description get localized title and description (EN, DE, FR, IT) from the Guide Expert. Ensure each guide has a slug before running.
        </Typography>
        <Button variant="outlined" onClick={loadIncomplete} disabled={loadingIncomplete}>
          {loadingIncomplete ? "Loading…" : "List guides needing content"}
        </Button>
        {incomplete !== null && (
          <div className="mt-4">
            {incomplete.length === 0 ? (
              <Typography variant="body2" className="text-green-600">
                All guides have English title and description.
              </Typography>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Typography variant="body2">{incomplete.length} guide(s) need content:</Typography>
                  <Button
                    variant="filled"
                    size="small"
                    color="primary"
                    onClick={runAll}
                    disabled={!!running}
                  >
                    {running === "all" ? "Running…" : "Run all"}
                  </Button>
                </div>
                <ul className="list-disc list-inside space-y-1 max-h-48 overflow-y-auto">
                  {incomplete.map((d) => (
                    <li key={d._id} className="flex items-center gap-2">
                      <span>{d.name || d._id}</span>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => runGuideExpert(d._id)}
                        disabled={!!running}
                      >
                        {running === d._id ? "Running…" : "Run"}
                      </Button>
                    </li>
                  ))}
                </ul>
                <BatchResultBar
                  label="Last batch"
                  results={batchResults}
                  onRetryFailed={retryFailed}
                  retryDisabled={!!running}
                />
              </>
            )}
          </div>
        )}
      </Paper>
    </div>
  );
}
